import { Clock } from 'lucide-react';

import { Badge, Button, cn } from '@sandtech-hub-monorepo/ui-kit';

import {
  CatalogueProgram,
  ProgramCta,
  programDescription,
  programImage,
  programTitle,
} from '../../../data/programs';
import { plural } from '../../../utils/plural';
import { CatalogueView } from './CatalogueFilterBar';

interface Props {
  program: CatalogueProgram;
  layout?: CatalogueView;
  /**
   * What the primary button says and whether it acts. Computed by the caller
   * rather than here, so one decision feeds both the label and the
   * destination behind `onPrimaryAction`.
   */
  cta: ProgramCta;
  onViewDetails: () => void;
  onPrimaryAction: () => void;
}

export const ProgramCard = ({
  program,
  layout = 'grid',
  cta,
  onViewDetails,
  onPrimaryAction,
}: Props) => {
  const isList = layout === 'list';

  const image = (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden rounded-xl',
        isList
          ? 'aspect-[16/10] w-full sm:w-64 md:w-72'
          : 'aspect-[16/9] w-full'
      )}
    >
      <img
        src={programImage(program)}
        alt={programTitle(program)}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* No badge for a program with no category — better an
          empty corner than an invented label. */}
      {program.category && (
        <Badge className="absolute left-3 top-3 rounded-full border-0 bg-brand-yellow px-3 py-1.5 text-sm font-normal text-foreground hover:bg-brand-yellow">
          {program.category}
        </Badge>
      )}
    </div>
  );

  const durationLabel =
    program.hours != null
      ? `~${program.hours} ${plural(program.hours, 'hr', 'hrs')}`
      : undefined;

  const meta = durationLabel ? (
    <div className="flex items-center gap-2 text-foreground">
      <Clock className="h-5 w-5 shrink-0" />
      <span className="font-medium">{durationLabel}</span>
    </div>
  ) : null;

  const title = (
    <h3
      className={cn(
        'font-medium leading-tight text-heading',
        isList ? 'text-xl' : 'text-2xl'
      )}
    >
      <button
        type="button"
        onClick={onViewDetails}
        className="text-left hover:underline"
      >
        {programTitle(program)}
      </button>
    </h3>
  );

  const description = (
    <p
      className={cn(
        'text-muted-foreground',
        isList ? 'line-clamp-2' : 'flex-1'
      )}
    >
      {programDescription(program)}
    </p>
  );

  const flair = (
    <div
      className={cn(
        'flex w-full flex-col gap-4 pt-1',
        // List cards size their buttons to content and can afford to go side
        // by side at `sm` — the viewport is a fine proxy for their width.
        // Grid cards can't use a viewport breakpoint at all: 2- and
        // 3-column rows are often narrower than the buttons need regardless
        // of how wide the page is, so they key off the card's own measured
        // width via a container query instead (see styles.css).
        isList
          ? 'mt-auto sm:w-fit sm:flex-row sm:items-center'
          : 'program-grid-card-actions'
      )}
    >
      <Button
        variant="outline"
        onClick={onViewDetails}
        className={cn(
          'w-full rounded-full px-8',
          isList ? 'sm:w-auto' : 'program-grid-card-action'
        )}
      >
        View details
      </Button>

      <Button
        onClick={onPrimaryAction}
        disabled={cta.disabled}
        className={cn(
          'w-full rounded-full px-8',
          isList ? 'sm:w-auto' : 'program-grid-card-action'
        )}
      >
        {cta.label}
      </Button>
    </div>
  );

  if (isList) {
    return (
      <div className="flex flex-col gap-4 rounded-xl border border-border-secondary bg-card p-4 sm:flex-row sm:gap-5 sm:p-5">
        {image}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {meta}
          {title}
          {description}
          {flair}
        </div>
      </div>
    );
  }

  return (
    <div className="program-grid-card-actions-container flex h-full flex-col gap-4 rounded-xl border border-border-secondary bg-card p-6">
      {image}
      {meta}
      {title}
      {description}
      {flair}
    </div>
  );
};
